import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { formatDate, formatDay } from 'utils/formatDate';
import { activityCount, activityDay } from 'pages/AdminTechnicians/csv';
import type { ActivityMission, TechnicianRecordActivity } from 'api/techniciansApi';

interface ActivitySectionProps {
  activity?: TechnicianRecordActivity | null;
}

const LABEL = 'text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400';

/** A mission the card can name, or null — an older API sends neither field, a broken one junk. */
const usableMission = (mission: ActivityMission | null | undefined): ActivityMission | null =>
  mission && typeof mission.issueKey === 'string' && mission.issueKey ? mission : null;

/** `desk · store · type`, the missing parts skipped, then the key as a link to the mission. */
const missionLine = (mission: ActivityMission): ReactNode[] => {
  const context = [mission.serviceDeskName, mission.store, mission.requestType].filter(Boolean).join(' · ');
  return [
    context && (
      <span key="context" className="text-zinc-900 dark:text-zinc-50">
        {context}
      </span>
    ),
    <Link
      key="link"
      to={`/admin/issues/${encodeURIComponent(mission.issueKey)}`}
      className="font-mono text-xs text-zinc-600 underline-offset-2 hover:underline focus-visible:underline dark:text-zinc-300"
    >
      {mission.issueKey}
    </Link>,
  ];
};

/**
 * Read-only, like the ratings beside it. `0` is a measured count and shown as such — only a
 * missing or malformed payload falls back to `—`. The two rows name the missions behind the
 * numbers; only the ticket key is a link, the count and the date stay aggregates.
 */
const ActivitySection = ({ activity }: ActivitySectionProps) => {
  const { t } = useTranslation();
  const count = activityCount(activity);
  const day = activityDay(activity);
  const last = usableMission(activity?.lastMission);
  const pending = usableMission(activity?.pendingRequest);

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4" aria-hidden="true" />
          {t('Activity')}
        </CardTitle>
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          {t('Counted since the assignment circuit went live.')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-800 sm:max-w-xs">
          <dt className={LABEL}>{t('Interventions carried out')}</dt>
          <dd className="m-0 mt-1 flex items-baseline gap-2">
            <span className="text-lg font-semibold tabular-nums text-zinc-900 dark:text-zinc-50">
              {count ?? '—'}
            </span>
            {count === null && (
              <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('Not available')}</span>
            )}
          </dd>
        </dl>
        <dl className="divide-y divide-zinc-200 rounded-lg border border-zinc-200 text-sm dark:divide-zinc-800 dark:border-zinc-800">
          <div className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-baseline sm:gap-4">
            <dt className={`${LABEL} sm:w-48 sm:shrink-0`}>{t('Last intervention')}</dt>
            <dd className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-zinc-500 dark:text-zinc-400">
              {last && missionLine(last)}
              <span className="tabular-nums">{day ? formatDay(day) : '—'}</span>
            </dd>
          </div>
          <div className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-baseline sm:gap-4">
            <dt className={`${LABEL} sm:w-48 sm:shrink-0`}>{t('Pending solicitation')}</dt>
            <dd className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-zinc-500 dark:text-zinc-400">
              {pending ? (
                <>
                  {missionLine(pending)}
                  {pending.sentAt && (
                    <span className="tabular-nums">{t('sent on {{date}}', { date: formatDate(pending.sentAt) })}</span>
                  )}
                </>
              ) : (
                t('No pending solicitation')
              )}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
};

export default ActivitySection;
